import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Rotate3d } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import api from "../lib/api";

export function StlViewerButton({ file }) {
  const [open, setOpen] = useState(false);
  if (!/\.stl$/i.test(file.filename || "")) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`file-view3d-${file.id}`}
          className="ml-3 inline-flex items-center gap-1 text-xs font-bold text-violet-600 hover:text-violet-800 shrink-0">
          <Rotate3d className="h-3.5 w-3.5" /> View 3D
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl" data-testid="stl-viewer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-lg truncate pr-8">{file.filename}</DialogTitle>
        </DialogHeader>
        {open && <StlCanvas fileId={file.id} />}
        <p className="text-xs text-slate-400" data-testid="stl-viewer-hint">Drag to rotate · scroll to zoom · right-drag to pan</p>
      </DialogContent>
    </Dialog>
  );
}

function StlCanvas({ fileId }) {
  const mountRef = useRef(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let disposed = false, renderer, controls, frameId;
    const mount = mountRef.current;
    (async () => {
      try {
        const res = await api.get(`/files/${fileId}/download`, { responseType: "arraybuffer" });
        if (disposed || !mount) return;
        const geom = new STLLoader().parse(res.data);
        geom.computeVertexNormals();
        geom.center();
        geom.computeBoundingSphere();
        const radius = geom.boundingSphere?.radius || 50;
        const w = mount.clientWidth || 640, h = mount.clientHeight || 460;
        const scene = new THREE.Scene();
        scene.background = new THREE.Color("#0B0F19");
        const camera = new THREE.PerspectiveCamera(45, w / h, radius / 100, radius * 30);
        camera.up.set(0, 0, 1);
        camera.position.set(0, -radius * 2.4, radius * 1.5);
        renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(w, h);
        mount.appendChild(renderer.domElement);
        const mesh = new THREE.Mesh(geom, new THREE.MeshStandardMaterial({ color: "#e9edf2", metalness: 0.1, roughness: 0.5 }));
        scene.add(mesh);
        scene.add(new THREE.HemisphereLight(0xffffff, 0x1e2a3a, 1.2));
        const key = new THREE.DirectionalLight(0xffffff, 1.1);
        key.position.set(radius * 2, -radius * 2, radius * 3);
        scene.add(key);
        const rim = new THREE.DirectionalLight(0x00e5ff, 0.35);
        rim.position.set(-radius * 2, radius * 2, -radius);
        scene.add(rim);
        controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        const animate = () => {
          frameId = requestAnimationFrame(animate);
          controls.update();
          renderer.render(scene, camera);
        };
        animate();
        setState("ready");
      } catch (e) {
        if (!disposed) setState("error");
      }
    })();
    return () => {
      disposed = true;
      cancelAnimationFrame(frameId);
      controls?.dispose();
      if (renderer) { renderer.dispose(); renderer.domElement.remove(); }
    };
  }, [fileId]);

  return (
    <div className="relative w-full h-[460px] rounded-xl overflow-hidden bg-[#0B0F19]" data-testid="stl-canvas-wrap">
      <div ref={mountRef} className="absolute inset-0" />
      {state === "loading" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3" data-testid="stl-loading">
          <div className="h-8 w-8 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
          <p className="text-xs text-slate-400">Loading 3D model…</p>
        </div>
      )}
      {state === "error" && (
        <div className="absolute inset-0 flex items-center justify-center" data-testid="stl-error">
          <p className="text-sm text-red-400">Could not load this STL file.</p>
        </div>
      )}
    </div>
  );
}
